import { useEffect, useMemo, useState } from 'react';
import { getApiBase } from '../lib/apiClient';
import { useAuth } from '../context/AuthContext';
import { toast } from '../hooks/use-toast';
import { FilterableColumnTable, type FilterableColumnDef } from './FilterableColumnTable';
import {
  createReportPdf, preloadPdfLogo, drawSectionBanner, drawKpiTiles, drawCallout,
  drawTable, drawEmptyState, drawClosingFooter, finalizeFooters, savePdf,
  fmtGeneratedAt, severityCell, COLORS,
  type DocCtx, type KpiTile, type TableCell,
} from '../lib/pdfTemplate';
import { fmtTs, getTz } from '../lib/globalTz';

type Lookback = '1h' | '4h' | '8h' | '12h' | '24h' | '7d' | '30d';
type HealthStatus = 'Healthy' | 'Warning' | 'Critical' | 'Unknown';
type IssueSeverity = 'Low' | 'Medium' | 'High' | 'Critical';

interface HealthCard { id: string; label: string; value: string | number; status: HealthStatus; helper: string; }
interface TableFreshness { table: string; connector: string; lastEventTime: string | null; minutesSilent: number | null; events: number; avgIngestionDelayMinutes: number | null; p95IngestionDelayMinutes: number | null; status: HealthStatus; recommendation: string; }
interface ConnectorStatus { id: string; name: string; tables: string[]; lastEventTime: string | null; minutesSilent: number | null; events: number; status: HealthStatus; details: string; }
interface HealthIssue { id: string; type: string; severity: IssueSeverity; title: string; details: string; affectedObject: string; evidence: string[]; recommendation: string; }
interface DefenderApiHealth { status: HealthStatus; httpStatus: number | null; latencyMs: number | null; endpoint: string; message: string; }
interface ArmResourceStatus { name: string; type: 'DCR' | 'DCE'; location: string; provisioningState: string; status: HealthStatus; id: string; }
interface QueryTable { id: string; title: string; columns: string[]; rows: Record<string, unknown>[]; warnings: string[]; skipped?: boolean; }
interface ConnectorHealthPayload {
  ok: true;
  customerId: string;
  customerName: string;
  lookback: Lookback;
  generatedAt: string;
  expiresAt: string | null;
  cacheHit?: boolean;
  score: { value: number; level: IssueSeverity; summary: string };
  cards: HealthCard[];
  tableFreshness: TableFreshness[];
  connectors: ConnectorStatus[];
  defenderApi: DefenderApiHealth;
  dcrDce: ArmResourceStatus[];
  issues: HealthIssue[];
  recommendations: Array<{ priority: IssueSeverity; title: string; details: string; owner: string }>;
  tables: QueryTable[];
  diagnostics: string[];
}
interface Props { customerId?: string; customerName?: string; }

function text(value: unknown, max = 180): string {
  if (value === null || value === undefined) return '';
  const raw = typeof value === 'string' ? value : JSON.stringify(value);
  const clean = raw.replace(/\s+/g, ' ').trim();
  return clean.length > max ? `${clean.slice(0, max - 1)}…` : clean;
}

function statusClass(status: HealthStatus): string {
  if (status === 'Critical') return 'border-red-300 bg-red-50 text-red-700 dark:border-red-500/50 dark:bg-red-500/10 dark:text-red-100';
  if (status === 'Warning') return 'border-amber-300 bg-amber-50 text-amber-700 dark:border-amber-500/50 dark:bg-amber-500/10 dark:text-amber-100';
  if (status === 'Healthy') return 'border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-500/50 dark:bg-emerald-500/10 dark:text-emerald-100';
  return 'border-slate-300 bg-slate-100 text-slate-700 dark:border-slate-500/50 dark:bg-slate-500/10 dark:text-slate-100';
}

function severityClass(severity: IssueSeverity): string {
  if (severity === 'Critical') return 'bg-red-600 text-white';
  if (severity === 'High') return 'bg-orange-500 text-white';
  if (severity === 'Medium') return 'bg-amber-500 text-slate-950';
  return 'bg-sky-500 text-white';
}

function scoreGradient(score: number): string {
  if (score < 45) return 'from-red-600 via-red-500 to-orange-500';
  if (score < 70) return 'from-orange-500 via-amber-500 to-yellow-400';
  if (score < 85) return 'from-yellow-500 via-lime-500 to-emerald-500';
  return 'from-emerald-500 via-cyan-500 to-blue-500';
}

function downloadText(filename: string, content: string, type: string) {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function drawAiParagraphs(ctx: DocCtx, markdown: string): void {
  const { pdf } = ctx;
  const lines = markdown.split(/\r?\n/).slice(0, 120);
  for (const raw of lines) {
    const line = raw.trim();
    if (!line) { ctx.y += 2; continue; }
    if (ctx.y > 270) { pdf.addPage(); ctx.y = 14; }
    if (line.startsWith('#')) {
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(10.5);
      pdf.setTextColor(...COLORS.primaryDark);
      pdf.text(line.replace(/^#+\s*/, ''), 14, ctx.y + 4);
      ctx.y += 7;
      continue;
    }
    const bullet = /^(?:[-*]|\d+\.)\s+(.*)$/.exec(line);
    const printable = bullet ? `• ${bullet[1]}` : line.replace(/\*\*/g, '');
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(8.3);
    pdf.setTextColor(...COLORS.ink);
    const wrapped = pdf.splitTextToSize(printable, 182);
    for (const w of wrapped) {
      if (ctx.y > 270) { pdf.addPage(); ctx.y = 14; }
      pdf.text(String(w), 14, ctx.y + 4);
      ctx.y += 4.2;
    }
  }
  ctx.y += 2;
}

async function exportPdf(payload: ConnectorHealthPayload, aiAnalysis: string | null, filenameBase: string): Promise<void> {
  await preloadPdfLogo();
  const tz = getTz();
  const ctx = createReportPdf({
    title: 'Connector Health + Data Freshness Guardian',
    customerName: payload.customerName,
    periodLabel: `${payload.lookback} lookback`,
    generatedLabel: fmtGeneratedAt(payload.generatedAt, tz),
  });

  drawSectionBanner(ctx, 'Platform Health Overview', 'primary');
  const tiles: KpiTile[] = payload.cards.map((c) => ({
    value: String(c.value),
    label: c.label,
    tone: c.status === 'Critical' ? 'danger' : c.status === 'Warning' ? 'warning' : c.status === 'Healthy' ? 'success' : 'default',
  }));
  drawKpiTiles(ctx, tiles);

  drawCallout(ctx, {
    tone: payload.score.value < 50 ? 'critical' : payload.score.value < 70 ? 'warning' : 'success',
    title: `Data Trust Score: ${payload.score.value}/100 (${payload.score.level})`,
    body: payload.score.summary,
  });

  if (aiAnalysis) {
    drawSectionBanner(ctx, 'AI Analysis', 'primary');
    drawAiParagraphs(ctx, aiAnalysis.slice(0, 3500));
  }

  drawSectionBanner(ctx, 'Top Health Issues', 'orange');
  if (payload.issues.length) {
    const issueRows: TableCell[][] = payload.issues.slice(0, 18).map((issue) => [
      severityCell(issue.severity),
      text(issue.title, 40),
      text(issue.affectedObject, 30),
      text(`${issue.details} Recommendation: ${issue.recommendation}`, 150),
    ]);
    drawTable(ctx, {
      head: ['Severity', 'Title', 'Affected Object', 'Details / Recommendation'],
      body: issueRows,
      columnStyles: {
        0: { cellWidth: 18, halign: 'center' },
        1: { cellWidth: 40 },
        2: { cellWidth: 30 },
        3: { cellWidth: 'auto' },
      },
      minRowHeight: 8,
    });
  } else {
    drawEmptyState(ctx, 'No health issues found for the selected lookback.');
  }

  drawSectionBanner(ctx, 'Table Freshness', 'primary');
  if (payload.tableFreshness.length) {
    const freshnessRows: TableCell[][] = payload.tableFreshness.slice(0, 25).map((row) => [
      row.status,
      text(row.table, 40),
      row.lastEventTime ? fmtTs(row.lastEventTime) : 'Unknown',
      row.minutesSilent === null ? 'n/a' : String(row.minutesSilent),
      String(row.events),
    ]);
    drawTable(ctx, {
      head: ['Status', 'Table', 'Last Event', 'Silent (min)', 'Events'],
      body: freshnessRows,
      columnStyles: {
        0: { cellWidth: 20, halign: 'center' },
        1: { cellWidth: 'auto' },
        2: { cellWidth: 32, halign: 'center' },
        3: { cellWidth: 22, halign: 'center' },
        4: { cellWidth: 18, halign: 'center' },
      },
      minRowHeight: 8,
    });
  } else {
    drawEmptyState(ctx, 'No table freshness data for the selected lookback.');
  }

  drawClosingFooter(ctx);
  finalizeFooters(ctx);
  savePdf(ctx, filenameBase);
}

export function ConnectorHealthGuardianTab({ customerId, customerName }: Props) {
  const { canCapability } = useAuth();
  const [lookback, setLookback] = useState<Lookback>('24h');
  const [payload, setPayload] = useState<ConnectorHealthPayload | null>(null);
  const [selectedTableId, setSelectedTableId] = useState('table_freshness');
  const [loading, setLoading] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [exportBusy, setExportBusy] = useState<string | null>(null);
  const [aiAnalysis, setAiAnalysis] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const canRead = canCapability('connector_health', 'read');
  const canRefresh = canCapability('connector_health', 'refresh');
  const canAi = canCapability('connector_health', 'ai_analysis');
  const canExport = canCapability('connector_health', 'export');

  async function load(refresh = false) {
    if (!customerId) return;
    setLoading(true); setError(null);
    try {
      const url = `${getApiBase()}/connector-health/summary?customerId=${encodeURIComponent(customerId)}&lookback=${encodeURIComponent(lookback)}${refresh ? '&refresh=true' : ''}`;
      const resp = await fetch(url, { credentials: 'include' });
      const parsed = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(parsed.message || parsed.error || `Connector Health load failed (${resp.status})`);
      const data = parsed as ConnectorHealthPayload;
      setPayload(data); setAiAnalysis(null);
      setSelectedTableId(data.tables.find((t) => t.rows.length > 0)?.id ?? data.tables[0]?.id ?? 'table_freshness');
      toast({ title: data.cacheHit ? 'Connector Health loaded from cache' : 'Connector Health refreshed', description: data.score.summary });
    } catch (err) { setError(err instanceof Error ? err.message : String(err)); }
    finally { setLoading(false); }
  }

  useEffect(() => { if (customerId && canRead) void load(false); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [customerId]);

  async function generateAiAnalysis() {
    if (!customerId || !payload) return;
    setAiBusy(true); setError(null);
    try {
      const resp = await fetch(`${getApiBase()}/connector-health/ai-analysis`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ customerId, payload }) });
      const parsed = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(parsed.message || parsed.error || `AI analysis failed (${resp.status})`);
      setAiAnalysis(String(parsed.analysis ?? ''));
      toast({ title: parsed.fallback ? 'Fallback health analysis generated' : 'Connector Health AI analysis generated', description: parsed.model ?? 'AI analysis' });
    } catch (err) { setError(err instanceof Error ? err.message : String(err)); }
    finally { setAiBusy(false); }
  }

  async function exportData(format: 'pdf' | 'csv' | 'html') {
    if (!customerId || !payload) return;
    setExportBusy(format); setError(null);
    try {
      const resp = await fetch(`${getApiBase()}/connector-health/export`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ customerId, payload, format, aiAnalysis }) });
      const parsed = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(parsed.message || parsed.error || `Export failed (${resp.status})`);
      const filenameBase = String(parsed.filenameBase || `kloudsecure-connector-health-${customerId}`);
      if (format === 'csv') downloadText(`${filenameBase}.csv`, String(parsed.csv ?? ''), 'text/csv;charset=utf-8');
      if (format === 'html') downloadText(`${filenameBase}.html`, String(parsed.html ?? ''), 'text/html;charset=utf-8');
      if (format === 'pdf') await exportPdf(payload, aiAnalysis, filenameBase);
      toast({ title: `Connector Health ${format.toUpperCase()} exported`, description: customerName ?? customerId });
    } catch (err) { setError(err instanceof Error ? err.message : String(err)); }
    finally { setExportBusy(null); }
  }

  const selectedTable = useMemo(() => payload?.tables.find((table) => table.id === selectedTableId) ?? payload?.tables[0] ?? null, [payload, selectedTableId]);
  const tableColumns = useMemo<FilterableColumnDef<Record<string, unknown>>[]>(() => {
    const columns = selectedTable?.columns?.length ? selectedTable.columns : ['message'];
    return columns.map((column) => ({ key: column, header: column, headerText: column, value: (row) => row[column], render: (row) => text(row[column], 300) }));
  }, [selectedTable]);

  const freshnessColumns = useMemo<FilterableColumnDef<TableFreshness>[]>(() => [
    { key: 'status', header: 'Status', value: (r) => r.status, render: (r) => <span className={`rounded-full px-2 py-0.5 text-xs font-bold ${statusClass(r.status)}`}>{r.status}</span> },
    { key: 'table', header: 'Table', value: (r) => r.table, render: (r) => <span className="font-mono font-bold">{r.table}</span> },
    { key: 'connector', header: 'Connector', value: (r) => r.connector },
    { key: 'lastEventTime', header: 'Last Event', value: (r) => r.lastEventTime ?? '', render: (r) => r.lastEventTime ? fmtTs(r.lastEventTime) : 'Unknown' },
    { key: 'minutesSilent', header: 'Silent Min', value: (r) => r.minutesSilent ?? '', render: (r) => r.minutesSilent ?? 'n/a' },
    { key: 'events', header: 'Events', value: (r) => r.events },
    { key: 'p95IngestionDelayMinutes', header: 'P95 Delay', value: (r) => r.p95IngestionDelayMinutes ?? '', render: (r) => r.p95IngestionDelayMinutes === null ? 'n/a' : `${r.p95IngestionDelayMinutes}m` },
    { key: 'recommendation', header: 'Recommendation', value: (r) => r.recommendation, render: (r) => text(r.recommendation, 220) },
  ], []);

  const issueColumns = useMemo<FilterableColumnDef<HealthIssue>[]>(() => [
    { key: 'severity', header: 'Severity', value: (r) => r.severity, render: (r) => <span className={`rounded-full px-2 py-0.5 text-xs font-bold ${severityClass(r.severity)}`}>{r.severity}</span> },
    { key: 'type', header: 'Type', value: (r) => r.type },
    { key: 'title', header: 'Title', value: (r) => r.title, render: (r) => <span className="font-bold">{r.title}</span> },
    { key: 'affectedObject', header: 'Affected Object', value: (r) => r.affectedObject },
    { key: 'details', header: 'Details', value: (r) => r.details, render: (r) => text(r.details, 260) },
    { key: 'recommendation', header: 'Recommendation', value: (r) => r.recommendation, render: (r) => text(r.recommendation, 260) },
  ], []);

  if (!canRead) return <div className="rounded-2xl border border-amber-300 bg-amber-50 p-5 text-sm text-amber-800 dark:bg-amber-950/30 dark:text-amber-200 dark:border-amber-900">Connector Health Guardian is hidden by Permissions Master. Enable Connector Health Guardian → Read for this role.</div>;

  return <div className="space-y-5">
    <div className="relative overflow-hidden rounded-3xl border border-blue-400/30 bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 p-6 shadow-2xl">
      <div className="absolute inset-y-0 right-0 w-1/2 bg-[radial-gradient(circle_at_top_right,rgba(56,189,248,0.28),transparent_42%)]" />
      <div className="relative flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.35em] text-cyan-200/80 font-black">Platform Health</div>
          <h2 className="mt-1 text-3xl font-black text-white">Connector Health + Data Freshness Guardian</h2>
          <p className="mt-2 max-w-5xl text-sm text-blue-100/80">Validates whether SOC dashboards can be trusted by checking connector freshness, Defender API reachability, Logic App failures, ingestion drops/spikes, parser failures and DCR/DCE status.</p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <div><label className="text-xs font-semibold text-blue-100/80">Lookback</label><select value={lookback} onChange={(e) => setLookback(e.target.value as Lookback)} className="mt-1 rounded-xl border border-white/20 bg-slate-950/80 px-3 py-2 text-sm text-white"><option value="1h">1 hour</option><option value="4h">4 hours</option><option value="8h">8 hours</option><option value="12h">12 hours</option><option value="24h">24 hours</option><option value="7d">7 days</option><option value="30d">30 days</option></select></div>
          <button type="button" onClick={() => void load(false)} disabled={loading || !customerId} className="rounded-xl bg-blue-500 px-4 py-2 text-sm font-bold text-white hover:bg-blue-400 disabled:opacity-60">{loading ? 'Loading…' : 'Load'}</button>
          {canRefresh && <button type="button" onClick={() => void load(true)} disabled={loading || !customerId} className="rounded-xl border border-white/20 px-4 py-2 text-sm font-bold text-white hover:bg-white/10 disabled:opacity-60">Refresh</button>}
          {canAi && payload && <button type="button" onClick={() => void generateAiAnalysis()} disabled={aiBusy} className="rounded-xl border border-fuchsia-300/50 bg-fuchsia-500/20 px-4 py-2 text-sm font-bold text-fuchsia-50 hover:bg-fuchsia-500/30 disabled:opacity-60">{aiBusy ? 'Analyzing…' : 'AI Analysis'}</button>}
          {canExport && payload && <button type="button" onClick={() => void exportData('pdf')} disabled={!!exportBusy} className="rounded-xl border border-white/20 px-4 py-2 text-sm font-bold text-white hover:bg-white/10 disabled:opacity-60">Export PDF</button>}
          {canExport && payload && <button type="button" onClick={() => void exportData('csv')} disabled={!!exportBusy} className="rounded-xl border border-white/20 px-4 py-2 text-sm font-bold text-white hover:bg-white/10 disabled:opacity-60">Export CSV</button>}
          {canExport && payload && <button type="button" onClick={() => void exportData('html')} disabled={!!exportBusy} className="rounded-xl border border-white/20 px-4 py-2 text-sm font-bold text-white hover:bg-white/10 disabled:opacity-60">Export HTML</button>}
        </div>
      </div>
      {payload && <div className="relative mt-3 text-xs text-blue-100/75">Generated {fmtTs(payload.generatedAt)} · {payload.cacheHit ? 'Loaded from DB cache' : 'Freshly queried'}{payload.expiresAt ? ` · Cache expires ${fmtTs(payload.expiresAt)}` : ''}</div>}
      {error && <div className="relative mt-4 rounded-xl border border-red-300 bg-red-500/20 px-4 py-3 text-sm text-red-50">{error}</div>}
    </div>

    {!customerId && <div className="rounded-2xl border border-dashed border-border bg-card/60 p-10 text-center text-muted-foreground">Select a customer to load Connector Health.</div>}

    {payload && <>
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <div className={`rounded-2xl bg-gradient-to-br ${scoreGradient(payload.score.value)} p-5 text-white shadow-lg`}><div className="text-xs uppercase tracking-wide font-bold opacity-90">Data Trust Score</div><div className="mt-2 text-5xl font-black">{payload.score.value}/100</div><div className="font-bold">{payload.score.level}</div><p className="mt-2 text-xs opacity-90">Dashboard reliability and pipeline confidence.</p></div>
        {payload.cards.filter((c) => c.id !== 'score').slice(0, 7).map((card) => <div key={card.id} className={`rounded-2xl border p-5 shadow-sm ${statusClass(card.status)}`}><div className="text-xs uppercase font-bold opacity-80">{card.label}</div><div className="mt-2 text-3xl font-black">{card.value}</div><div className="mt-1 text-xs opacity-80">{card.helper}</div></div>)}
      </div>

      <div className="grid gap-4 xl:grid-cols-[1fr_1fr]">
        <div className="rounded-2xl border border-border bg-card p-5 shadow-sm"><h3 className="text-lg font-black text-foreground">Guardian Summary</h3><p className="mt-2 text-sm text-muted-foreground">{payload.score.summary}</p>{aiAnalysis && <div className="mt-4 whitespace-pre-wrap rounded-xl border border-fuchsia-400/30 bg-fuchsia-500/10 p-4 text-sm text-foreground">{aiAnalysis}</div>}<div className="mt-4 rounded-xl border border-border bg-background p-4"><div className="text-sm font-black">Example impact statement</div><p className="mt-1 text-sm text-muted-foreground">{payload.issues[0]?.details || 'No current freshness issue detected. Dashboards and incident correlation appear to have usable pipeline coverage for the selected lookback.'}</p></div></div>
        <div className="rounded-2xl border border-border bg-card p-5 shadow-sm"><h3 className="text-lg font-black text-foreground">Defender API Health</h3><div className={`mt-3 rounded-xl border p-4 ${statusClass(payload.defenderApi.status)}`}><div className="text-2xl font-black">{payload.defenderApi.status}</div><div className="mt-1 text-sm opacity-90">{payload.defenderApi.message}</div><div className="mt-2 text-xs opacity-75">HTTP {payload.defenderApi.httpStatus ?? 'n/a'} · Latency {payload.defenderApi.latencyMs ?? 'n/a'}ms</div></div><h4 className="mt-4 text-sm font-black text-foreground">DCR / DCE Status</h4><div className="mt-2 grid gap-2 md:grid-cols-2">{payload.dcrDce.slice(0, 8).map((r) => <div key={r.id || `${r.type}-${r.name}`} className={`rounded-xl border p-3 ${statusClass(r.status)}`}><div className="font-bold">{r.type} · {r.name}</div><div className="text-xs opacity-80">{r.provisioningState} · {r.location}</div></div>)}{payload.dcrDce.length === 0 && <div className="rounded-xl border border-border bg-background p-3 text-sm text-muted-foreground">No DCR/DCE resources discovered or ARM status was unavailable.</div>}</div></div>
      </div>

      <div className="rounded-2xl border border-border bg-card p-5 shadow-sm"><div className="flex items-center justify-between gap-3"><div><h3 className="text-lg font-black text-foreground">Priority Health Issues</h3><p className="text-sm text-muted-foreground">Use the column ... menu for filters, checklist values and autosize.</p></div><span className="rounded-full border border-border px-3 py-1 text-xs font-bold">{payload.issues.length} issue(s)</span></div><div className="mt-4"><FilterableColumnTable<HealthIssue> columns={issueColumns} rows={payload.issues} storageKey={`connector-health-issues-${customerId}`} rowKey={(row) => row.id} emptyMessage="No health issues for the current filters." maxHeightClassName="max-h-[520px]" /></div></div>

      <div className="grid gap-4 xl:grid-cols-[0.95fr_1.05fr]">
        <div className="rounded-2xl border border-border bg-card p-5 shadow-sm"><h3 className="text-lg font-black text-foreground">Connector Groups</h3><div className="mt-4 space-y-3">{payload.connectors.map((connector) => <div key={connector.id} className={`rounded-xl border p-4 ${statusClass(connector.status)}`}><div className="flex items-start justify-between gap-3"><div className="font-black">{connector.name}</div><span className="rounded-full border border-white/20 px-2 py-0.5 text-xs font-bold">{connector.status}</span></div><div className="mt-1 text-xs opacity-80">Tables: {connector.tables.join(', ')}</div><div className="mt-2 text-sm opacity-90">{connector.details}</div></div>)}</div></div>
        <div className="rounded-2xl border border-border bg-card p-5 shadow-sm"><h3 className="text-lg font-black text-foreground">Recommended Actions</h3><div className="mt-4 space-y-3">{payload.recommendations.map((rec, index) => <div key={`${rec.title}-${index}`} className="rounded-xl border border-border bg-background p-4"><div className="flex items-center gap-2"><span className={`rounded-full px-2 py-0.5 text-xs font-bold ${severityClass(rec.priority)}`}>{rec.priority}</span><div className="font-bold text-foreground">{rec.title}</div></div><div className="mt-2 text-sm text-muted-foreground">{rec.details}</div><div className="mt-2 text-xs text-muted-foreground">Owner: {rec.owner}</div></div>)}</div></div>
      </div>

      <div className="rounded-2xl border border-border bg-card p-5 shadow-sm"><h3 className="text-lg font-black text-foreground">Table-Level Data Freshness</h3><p className="text-sm text-muted-foreground">Last event time, ingestion delay and silent-table detection by workspace table.</p><div className="mt-4"><FilterableColumnTable<TableFreshness> columns={freshnessColumns} rows={payload.tableFreshness} storageKey={`connector-health-freshness-${customerId}`} rowKey={(row) => row.table} emptyMessage="No table freshness rows available." maxHeightClassName="max-h-[560px]" /></div></div>

      <div className="rounded-2xl border border-border bg-card p-5 shadow-sm"><div className="flex flex-wrap items-end justify-between gap-3"><div><h3 className="text-lg font-black text-foreground">Raw Health Evidence Tables</h3><p className="text-sm text-muted-foreground">Detailed KQL/API outputs used by the guardian. Use ... filters for drilldowns.</p></div><select value={selectedTableId} onChange={(event) => setSelectedTableId(event.target.value)} className="rounded-xl border border-border bg-background px-3 py-2 text-sm">{payload.tables.map((table) => <option key={table.id} value={table.id}>{table.title} ({table.rows.length})</option>)}</select></div>{selectedTable?.warnings?.length ? <div className="mt-4 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:bg-amber-950/30 dark:text-amber-200 dark:border-amber-900">{selectedTable.warnings.join(' ')}</div> : null}<div className="mt-4"><FilterableColumnTable<Record<string, unknown>> columns={tableColumns} rows={selectedTable?.rows ?? []} storageKey={`connector-health-raw-${customerId}-${selectedTable?.id ?? 'none'}`} rowKey={(row, index) => `${selectedTable?.id ?? 'row'}-${index}-${text(row[selectedTable?.columns[0] ?? ''])}`} emptyMessage="No rows for this health evidence table or current filters." maxHeightClassName="max-h-[560px]" /></div></div>

      {payload.diagnostics.length > 0 && <div className="rounded-2xl border border-amber-300 bg-amber-50 p-5 text-sm text-amber-800 dark:bg-amber-950/30 dark:text-amber-200 dark:border-amber-900"><strong>Partial data warnings:</strong><ul className="mt-2 list-disc pl-5">{payload.diagnostics.slice(0, 10).map((d, i) => <li key={i}>{d}</li>)}</ul></div>}
    </>}
  </div>;
}
